import { useEffect, useState } from 'react';

import { useOfflineQueue } from '../../../offline';
import { WEATHER_FORECAST_URL, parseWeatherForecast } from '../../../utils/weather';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 10000;

/**
 * Today's forecast for the Home weather card, or null (no location, offline,
 * or no successful request yet): the card then hides.
 * location: { latitude, longitude, label } from resolveWeatherLocation.
 */
const useHomeWeather = (location) => {
	const { isOnline } = useOfflineQueue();
	const [weather, setWeather] = useState(null);
	const latitude = location?.latitude;
	const longitude = location?.longitude;

	useEffect(() => {
		if (latitude === undefined || longitude === undefined) {
			setWeather(null);
			return undefined;
		}
		if (!isOnline) {
			return undefined;
		}

		let isActive = true;
		const query = [
			`latitude=${latitude}`,
			`longitude=${longitude}`,
			'current=temperature_2m,weather_code',
			'daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max',
			'timezone=auto',
			'forecast_days=1',
		].join('&');

		const loadWeather = async () => {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
			try {
				const response = await fetch(`${WEATHER_FORECAST_URL}?${query}`, { signal: controller.signal });
				const forecast = response.ok ? parseWeatherForecast(await response.json()) : null;
				if (isActive && forecast) {
					setWeather(forecast);
				}
			}
			catch (error) {
				// Weather is decorative; keep the last forecast on failure.
			}
			finally {
				clearTimeout(timeout);
			}
		};

		loadWeather();
		const refreshInterval = setInterval(loadWeather, REFRESH_INTERVAL_MS);

		return () => {
			isActive = false;
			clearInterval(refreshInterval);
		};
	}, [isOnline, latitude, longitude]);

	return isOnline ? weather : null;
};

export default useHomeWeather;
